export function accountStatusLabel(status?: string) {
  if (status === 'active') return 'Active'
  if (status === 'expired') return 'Expired'
  if (status === 'cancelled') return 'Stopped'
  return status || '—'
}

export function accountStatusColor(status?: string) {
  if (status === 'active') return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300'
  if (status === 'expired') return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300'
  if (status === 'cancelled') return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200'
  return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
}

export function billingLabel(account: {
  amount?: number | string | null
  currency?: string | null
  billingCycle?: string | null
}) {
  const cycle = account.billingCycle === 'monthly'
    ? 'month'
    : account.billingCycle === 'yearly'
      ? 'year'
      : account.billingCycle === 'one-time'
        ? 'one time'
        : ''
  if (account.amount == null || account.amount === '') return cycle === 'one time' ? 'One time' : cycle ? `Per ${cycle}` : '—'
  const amount = Number(account.amount)
  const money = Number.isFinite(amount)
    ? amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })
    : String(account.amount)
  const currency = account.currency || 'INR'
  if (!cycle || cycle === 'one time') return `${currency} ${money}${cycle ? ' · one time' : ''}`
  return `${currency} ${money} / ${cycle}`
}

type DateParts = { y: number; m: number; d: number }

function parseDateOnly(value?: string | null): DateParts | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''))
  if (!match) return null
  const y = Number(match[1])
  const m = Number(match[2])
  const d = Number(match[3])
  if (m < 1 || m > 12 || d < 1 || d > 31) return null
  return { y, m, d }
}

function daysInMonth(y: number, m: number) {
  return new Date(y, m, 0).getDate()
}

function withDay(y: number, m: number, day: number): DateParts {
  return { y, m, d: Math.min(day, daysInMonth(y, m)) }
}

function dateKey(parts: DateParts) {
  return `${parts.y}-${String(parts.m).padStart(2, '0')}-${String(parts.d).padStart(2, '0')}`
}

function stepRenewal(current: DateParts, cycle: 'monthly' | 'yearly', day: number): DateParts {
  if (cycle === 'yearly') return withDay(current.y + 1, current.m, day)
  const m = current.m === 12 ? 1 : current.m + 1
  const y = current.m === 12 ? current.y + 1 : current.y
  return withDay(y, m, day)
}

export function nextRenewalDate(
  account: { renewalDate?: string | null; billingCycle?: string | null },
  today = new Date(),
): Date | null {
  const cycle = account.billingCycle
  if (cycle !== 'monthly' && cycle !== 'yearly') return null
  const anchor = parseDateOnly(account.renewalDate)
  if (!anchor) return null

  const now = dateKey({ y: today.getFullYear(), m: today.getMonth() + 1, d: today.getDate() })
  let current = withDay(anchor.y, anchor.m, anchor.d)
  for (let step = 0; step < 600 && dateKey(current) < now; step += 1) {
    current = stepRenewal(current, cycle, anchor.d)
  }
  return new Date(current.y, current.m - 1, current.d, 12)
}

export function statusSummary(accounts: { status?: string }[]) {
  const counts = new Map<string, number>()
  accounts.forEach((account) => {
    const label = accountStatusLabel(account.status).toLowerCase()
    counts.set(label, (counts.get(label) || 0) + 1)
  })
  if (!counts.size) return 'No accounts'
  return Array.from(counts.entries()).map(([label, count]) => `${count} ${label}`).join(', ')
}
